import { defineStore } from 'pinia';
import notificationWrapper from '@/utils/helpers/notificationWrapper';
import { useProjectStore } from './projectStore';
import {
  emptyAddressesByType,
  type AddressesState,
  type CustomerAddress,
  type CustomerAddressPayload,
  type CustomerAddressesByType,
} from '@/types/Addresses';

export const useAddressStore = defineStore('address', {
  state: (): AddressesState => ({
    addresses: emptyAddressesByType(),
    addressesLoading: false,
    addressSaving: false,
  }),

  actions: {
    async getAddresses() {
      const projectStore = useProjectStore();
      if (!projectStore.projectIdSelected) {
        this.addresses = emptyAddressesByType();
        return this.addresses;
      }

      this.addressesLoading = true;
      try {
        const data = await window.appConnector.getAddresses(projectStore.projectIdSelected);
        this.addresses = data.addresses || emptyAddressesByType();
        this.addressesLoading = false;
        return this.addresses;
      } catch (error) {
        notificationWrapper('error', error.isAuthenticationError ? 'NOT_LOGGED' : 'DEFAULT');
        this.addressesLoading = false;
        throw error;
      }
    },

    setAddresses(addresses: CustomerAddressesByType) {
      this.addresses = addresses;
    },

    async submitAddress(addressId: number, payload: CustomerAddressPayload) {
      const projectStore = useProjectStore();
      if (!projectStore.projectIdSelected) return null;

      this.addressSaving = true;
      try {
        const data = await window.appConnector.patchAddress(
          projectStore.projectIdSelected,
          addressId,
          payload,
        );
        this.addresses = data.addresses || this.addresses;
        notificationWrapper('success', 'PATCH_ADDRESS');
        this.addressSaving = false;
        return (data.address as CustomerAddress) || null;
      } catch (error: any) {
        this.addressSaving = false;
        if (error?.status === 404) {
          notificationWrapper('error', 'PATCH_ADDRESS_404');
          await this.getAddresses();
          throw error;
        }
        notificationWrapper('error', error.isAuthenticationError ? 'NOT_LOGGED' : 'DEFAULT');
        throw error;
      }
    },
  },
});
